import { apiGet, apiPostFormRaw, API_BASE_URL, getTenantId } from './client';

export interface BankFileUploadResult {
  filesLoaded: number;
  importBatchId: string;
  bankId: number;
  valueDates: string[];
  preInventoryRows: number;
  messages: string[];
}

export interface PreInventoryDto {
  preInventoryId: number;
  valueDate: string;
  acc: string;
  balanceCode: string | null;
  assetId: number | null;
  assetName: string | null;
  qty: number | null;
  qty4Receive: number | null;
  crcyId4Receive: string | null;
  crcyId: string | null;
  rate: number | null;
  bankId: number;
  splitterCode: string | null;
  fileImportId: number | null;
  noseType: string | null;
}

// A registered bank-file import (קליטה) from the BankImportDefs registry — drives
// the per-import sector cards on the screen.
export interface BankImportDef {
  importId: number;
  importName: string;
  bankId: number;
  format: string;
  importType: string;
  sourceKind: string;
  filePattern: string | null;
  lastImportDate: string | null;
  isActive: boolean;
}

export function getBankImports(): Promise<BankImportDef[]> {
  return apiGet<BankImportDef[]>('/api/bank-imports');
}

// One recorded shred exception (dbo.BankImportExceptions) shown on the screen so
// the operator can review problems (e.g. UNDEFINED_ACCOUNTS, MISSING_SUBJECTS).
export interface BankImportException {
  exceptionId: number;
  valueDate: string;
  bankId: number;
  exceptionCode: string;
  severity: number;
  message: string;
  details: string | null;
  createdAt: string;
}

export function getBankImportExceptions(
  bankId?: number,
  valueDate?: string,
): Promise<BankImportException[]> {
  const params = new URLSearchParams();
  if (bankId != null) params.append('bankId', String(bankId));
  if (valueDate) params.append('valueDate', valueDate);
  const qs = params.toString();
  return apiGet<BankImportException[]>(
    `/api/bank-files/exceptions${qs ? `?${qs}` : ''}`,
  );
}

export async function uploadBankFiles(
  bankId: number,
  files: File[],
  importId?: number,
  valueDate?: string,
): Promise<BankFileUploadResult> {
  const form = new FormData();
  form.append('bankId', String(bankId));
  if (importId != null) {
    form.append('importId', String(importId));
  }
  // Optional as-of date override (yyyy-MM-dd). Banks whose file carries a date
  // derive it themselves; Mizrahi has no in-file date and REQUIRES this.
  if (valueDate) {
    form.append('valueDate', valueDate);
  }
  for (const file of files) {
    form.append('files', file);
  }
  const res = await apiPostFormRaw('/api/bank-files/upload', form);
  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    try {
      const data = await res.json();
      if (data && typeof data.message === 'string') {
        detail = data.message;
      } else if (data && typeof data.error === 'string') {
        detail = data.error;
      }
    } catch {
      // body wasn't JSON — keep status/statusText
    }
    throw new Error(`POST /api/bank-files/upload failed: ${detail}`);
  }
  return (await res.json()) as BankFileUploadResult;
}

// Downloads the sample/template file for an ingestion (GET returns an .xlsx attachment).
export async function downloadImportSample(importId: number): Promise<void> {
  // Tenant-scoped endpoint (BankImportDefs live in the tenant DB) — inject
  // X-Tenant-Id the way apiGet does, since this builds its own blob fetch.
  const headers: Record<string, string> = {};
  const tenantId = getTenantId();
  if (tenantId !== null) headers['X-Tenant-Id'] = String(tenantId);
  const res = await fetch(`${API_BASE_URL}/api/bank-files/sample/${importId}`, {
    credentials: 'include',
    headers,
  });
  if (!res.ok) throw new Error(`Sample download failed: ${res.status}`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'sample.xlsx';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function getPreInventory(
  bankId: number,
  valueDate?: string,
): Promise<PreInventoryDto[]> {
  const params = new URLSearchParams({ bankId: String(bankId) });
  if (valueDate) params.append('valueDate', valueDate);
  return apiGet<PreInventoryDto[]>(`/api/pre-inventory?${params.toString()}`);
}

// All PreInventory rows (no bank/date filter) — backs the standalone "מלאי קליטה"
// helper-table screen. The endpoint returns everything when no filter is passed.
export function getAllPreInventory(): Promise<PreInventoryDto[]> {
  return apiGet<PreInventoryDto[]>('/api/pre-inventory');
}
